import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { CourseProgressSchema } from '../course-progress/course-progress';
import { CourseSchema } from '../course/course';
import { TestAttemptStatusSchema } from '../test-attempt-status/test-attempt-status';

/**
 * What the member needs to know about a test **before** starting it — the
 * numbers that make matrix row 8's rule readable in advance rather than
 * discovered on exhaustion.
 */
export const MemberTestSummarySchema = z
  .object({
    id: UuidSchema,
    title: z.string(),
    passingScorePct: z.number().int().min(1).max(100),
    questionCount: z.number().int().nonnegative(),
    /** `null` means unlimited takes. */
    attemptsAllowed: z.number().int().nullable(),
    /** Every started attempt counts, expired ones included (row 8). */
    attemptsUsed: z.number().int().nonnegative(),
    /** The best score so far, or `null` if never scored. */
    bestScorePct: z.number().int().min(0).max(100).nullable(),
    /** Whether this member has ever passed — what makes row 9 visible. */
    hasPassed: z.boolean(),
    /** An attempt to resume, when one is open. */
    openAttemptId: UuidSchema.nullable(),
    lastAttemptStatus: TestAttemptStatusSchema.nullable(),
    /**
     * When a retake becomes possible (matrix rows 5–6).
     *
     * A timestamp rather than a "hours remaining" number: a duration computed on
     * the server and rendered minutes later on a phone is wrong by the latency,
     * and the client already has a clock and a formatter.
     */
    retakeAvailableAt: z.iso.datetime().nullable(),
  })
  .strict();

export type MemberTestSummary = z.infer<typeof MemberTestSummarySchema>;

/**
 * One row of the member's course catalog — the course, how far they are, and
 * what its test would cost them.
 *
 * Composed server-side rather than assembled by the client from three calls:
 * the catalog is the first screen of the module and a three-request render is a
 * three-spinner screen.
 */
export const CourseCatalogItemSchema = CourseSchema.omit({ isActive: true })
  .extend({
    progress: CourseProgressSchema,
    test: MemberTestSummarySchema.nullable(),
  })
  .strict();

export type CourseCatalogItem = z.infer<typeof CourseCatalogItemSchema>;

/**
 * A standalone test in the catalog (matrix row 4) — a test with no course to
 * belong to, offered on its own.
 *
 * Its own shape rather than a `CourseCatalogItem` with a null course, because a
 * client rendering a card needs to know which of the two it is holding, and a
 * nullable course id would put that decision in every renderer.
 */
export const StandaloneTestCatalogItemSchema = z
  .object({ test: MemberTestSummarySchema, intro: z.string() })
  .strict();

export type StandaloneTestCatalogItem = z.infer<typeof StandaloneTestCatalogItemSchema>;

/** The whole catalog: courses and the standalone tests, in one payload. */
export const CourseCatalogSchema = z
  .object({
    courses: z.array(CourseCatalogItemSchema),
    standaloneTests: z.array(StandaloneTestCatalogItemSchema),
  })
  .strict();

export type CourseCatalog = z.infer<typeof CourseCatalogSchema>;
